import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthImage } from "@/components/AuthImage";
import { IdCard, Shirt, FileText, Upload, Trash2, Download, Check } from "lucide-react";
import { toast } from "sonner";

const UNIFORM_FIELDS = [
  { key: "shirt", label: "Shirt / Hi-vis top" },
  { key: "pants", label: "Pants / Shorts" },
  { key: "boots", label: "Boots (NZ size)" },
  { key: "jacket", label: "Jacket" },
  { key: "hi_vis", label: "Hi-vis vest" },
  { key: "hard_hat", label: "Hard hat" },
];

async function uploadFile(file, category) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.storage_path || null;
}

export function fileViewUrl(path, download = false) {
  const token = localStorage.getItem("fl_token");
  return `${API}/files/view?path=${encodeURIComponent(path)}${token ? `&auth=${token}` : ""}${download ? "&download=1" : ""}`;
}

// --- Driver licence (self-edit) -------------------------------------------
export function LicenceCard({ me, onSaved }) {
  const [lic, setLic] = useState(me?.licence || {});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setLic({ ...lic, [k]: e.target.value });

  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { licence: { ...lic, ...patch } });
      setLic(data.licence || {});
      onSaved?.(data);
      toast.success("Licence details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const path = await uploadFile(f, "licences");
      await save({ photo_path: path });
    } catch { toast.error("Photo upload failed"); setBusy(false); }
  };

  return (
    <div className="hard-card p-5" data-testid="licence-card">
      <div className="flex items-center gap-2 mb-3">
        <IdCard className="w-4 h-4 text-[#FF5F15]" />
        <div className="label-tag">Driver licence</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Licence no.</label>
          <Input value={lic.number || ""} onChange={set("number")} placeholder="AB123456" className="mt-1 rounded-sm border-zinc-400 h-10" data-testid="licence-number-input" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Class(es)</label>
          <Input value={lic.licence_class || ""} onChange={set("licence_class")} placeholder="1, 2, W" className="mt-1 rounded-sm border-zinc-400 h-10" data-testid="licence-class-input" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Expiry</label>
          <Input type="date" value={lic.expiry || ""} onChange={set("expiry")} className="mt-1 rounded-sm border-zinc-400 h-10" data-testid="licence-expiry-input" />
        </div>
      </div>
      {lic.photo_path && (
        <div className="mt-3 border border-zinc-200 overflow-hidden max-w-[240px]">
          <AuthImage path={lic.photo_path} alt="Driver licence" className="w-full object-cover" />
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => save()} disabled={busy} className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase font-bold text-xs" data-testid="licence-save-btn">
          <Check className="w-3.5 h-3.5 mr-1" /> Save details
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-sm uppercase font-bold text-xs border-zinc-400" data-testid="licence-photo-btn">
          <Upload className="w-3.5 h-3.5 mr-1" /> {lic.photo_path ? "Replace photo" : "Upload photo"}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} data-testid="licence-photo-input" />
      </div>
    </div>
  );
}

// --- Uniform sizes (self-edit) ---------------------------------------------
export function UniformCard({ me, onSaved }) {
  const [sizes, setSizes] = useState(me?.uniform_sizes || {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", { uniform_sizes: sizes });
      setSizes(data.uniform_sizes || {});
      onSaved?.(data);
      toast.success("Uniform sizes saved — the office can see these for orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="hard-card p-5" data-testid="uniform-card">
      <div className="flex items-center gap-2 mb-1">
        <Shirt className="w-4 h-4 text-[#FF5F15]" />
        <div className="label-tag">Uniform sizes</div>
      </div>
      <p className="text-xs text-zinc-500 mb-3">The office uses these when ordering gear — keep them current.</p>
      <div className="grid grid-cols-2 gap-3">
        {UNIFORM_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{f.label}</label>
            <Input value={sizes[f.key] || ""} onChange={(e) => setSizes({ ...sizes, [f.key]: e.target.value })}
              placeholder="e.g. XL / 34 / 11" className="mt-1 rounded-sm border-zinc-400 h-10" data-testid={`uniform-${f.key}-input`} />
          </div>
        ))}
      </div>
      <Button size="sm" onClick={save} disabled={busy} className="mt-3 rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase font-bold text-xs" data-testid="uniform-save-btn">
        <Check className="w-3.5 h-3.5 mr-1" /> Save sizes
      </Button>
    </div>
  );
}

// --- My contract (worker read-only) ----------------------------------------
export function MyContractCard({ me }) {
  return (
    <div className="hard-card p-5" data-testid="my-contract-card">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#FF5F15]" />
        <div className="label-tag">My employment contract</div>
      </div>
      {me?.contract_path ? (
        <div>
          <div className="text-sm font-bold">{me.contract_name || "Employment contract"}</div>
          {me.contract_uploaded_at && (
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
              Uploaded {new Date(me.contract_uploaded_at).toLocaleDateString("en-NZ")}{me.contract_uploaded_by ? ` by ${me.contract_uploaded_by}` : ""}
            </div>
          )}
          <a href={fileViewUrl(me.contract_path, true)}
            className="mt-3 inline-flex items-center gap-1.5 bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm"
            data-testid="contract-download-btn">
            <Download className="w-3.5 h-3.5" /> Download contract
          </a>
        </div>
      ) : (
        <p className="text-sm text-zinc-500 italic">No contract uploaded yet — the office will add it here once it&apos;s ready.</p>
      )}
    </div>
  );
}

// --- Office view: worker HR details + contract upload -----------------------
export function TeamMemberHr({ member, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const lic = member.licence || {};
  const sizes = member.uniform_sizes || {};

  const onContract = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)"); return; }
    setBusy(true);
    try {
      const path = await uploadFile(f, "contracts");
      await api.patch(`/users/${member.id}/hr`, { contract_path: path, contract_name: f.name });
      toast.success(`Contract uploaded for ${member.name}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const removeContract = async () => {
    if (!window.confirm(`Remove ${member.name}'s contract?`)) return;
    setBusy(true);
    try {
      await api.patch(`/users/${member.id}/hr`, { contract_path: "" });
      toast.success("Contract removed");
      onChanged?.();
    } catch { toast.error("Remove failed"); } finally { setBusy(false); }
  };

  const expiringSoon = lic.expiry && (new Date(lic.expiry) - Date.now()) / 86400000 < 60;

  return (
    <div className="space-y-4" data-testid={`member-hr-${member.id}`}>
      <div>
        <div className="label-tag mb-2">Driver licence</div>
        {lic.number || lic.expiry || lic.photo_path ? (
          <div className="text-sm space-y-1">
            {lic.number && <div><span className="text-zinc-500">Number:</span> <b className="font-mono">{lic.number}</b></div>}
            {lic.licence_class && <div><span className="text-zinc-500">Class:</span> <b>{lic.licence_class}</b></div>}
            {lic.expiry && (
              <div>
                <span className="text-zinc-500">Expires:</span>{" "}
                <b className={expiringSoon ? "text-red-600" : ""}>{new Date(lic.expiry).toLocaleDateString("en-NZ")}</b>
                {expiringSoon && <span className="ml-1.5 text-[10px] font-bold uppercase text-red-600">Expiring soon</span>}
              </div>
            )}
            {lic.photo_path && (
              <div className="mt-2 border border-zinc-200 overflow-hidden max-w-[220px]">
                <AuthImage path={lic.photo_path} alt="Licence" className="w-full object-cover" />
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-zinc-400 italic">Not provided yet — worker adds this on their Profile page.</p>
        )}
      </div>

      <div>
        <div className="label-tag mb-2">Uniform sizes</div>
        {Object.keys(sizes).length ? (
          <div className="flex flex-wrap gap-1.5">
            {UNIFORM_FIELDS.filter((f) => sizes[f.key]).map((f) => (
              <span key={f.key} className="border border-zinc-300 bg-zinc-50 px-2 py-1 text-[11px] font-bold uppercase tracking-wide" data-testid={`hr-size-${f.key}`}>
                {f.label.split(" ")[0]}: {sizes[f.key]}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-zinc-400 italic">Not provided yet.</p>
        )}
      </div>

      <div>
        <div className="label-tag mb-2">Employment contract</div>
        {member.contract_path ? (
          <div className="flex flex-wrap items-center gap-2">
            <a href={fileViewUrl(member.contract_path, true)}
              className="inline-flex items-center gap-1.5 bg-[#09090B] hover:bg-zinc-800 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm"
              data-testid={`hr-contract-download-${member.id}`}>
              <Download className="w-3 h-3" /> {member.contract_name || "Contract"}
            </a>
            <button onClick={removeContract} disabled={busy} className="text-zinc-400 hover:text-red-600" data-testid={`hr-contract-remove-${member.id}`} aria-label="Remove contract">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}
            className="rounded-sm uppercase font-bold text-xs border-zinc-400" data-testid={`hr-contract-upload-${member.id}`}>
            <Upload className="w-3.5 h-3.5 mr-1" /> {busy ? "Uploading…" : "Upload contract"}
          </Button>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,image/*" hidden onChange={onContract} />
      </div>
    </div>
  );
}
